import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, Tag, Receipt, Plus, Trash2, Truck, Store } from "lucide-react";
import api, { brl, mediaUrl, timeAgo, formatApiError } from "@/lib/api";
import { Pill, Stars, ORDER_STATUS, LISTING_STATUS } from "@/components/market/MarketUI";
import { Loader } from "@/components/Loader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function MarketPanel() {
  const [seller, setSeller] = useState(null);
  useEffect(() => { api.get("/market/seller/me").then((r) => setSeller(r.data)); }, []);
  if (!seller) return <Loader full />;
  const isSeller = seller.status === "approved";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <p className="eyebrow text-primary">Mercado</p>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight">Minhas compras e vendas</h1>
      <Tabs defaultValue="orders" className="mt-6">
        <TabsList className="rounded-full flex-wrap h-auto">
          <TabsTrigger value="orders" data-testid="panel-tab-orders" className="rounded-full gap-1.5"><ShoppingBag className="h-4 w-4" /> Compras</TabsTrigger>
          {isSeller && <TabsTrigger value="sales" data-testid="panel-tab-sales" className="rounded-full gap-1.5"><Receipt className="h-4 w-4" /> Vendas</TabsTrigger>}
          {isSeller && <TabsTrigger value="listings" data-testid="panel-tab-listings" className="rounded-full gap-1.5"><Tag className="h-4 w-4" /> Meus anúncios</TabsTrigger>}
        </TabsList>
        <TabsContent value="orders" className="mt-6"><Orders /></TabsContent>
        {isSeller && <TabsContent value="sales" className="mt-6"><Sales /></TabsContent>}
        {isSeller && <TabsContent value="listings" className="mt-6"><MyListings /></TabsContent>}
      </Tabs>
      {!isSeller && (
        <div className="mt-8 rounded-2xl border border-dashed border-border p-6 text-center">
          <p className="font-semibold">Quer vender no Mercado?</p>
          <Button asChild className="mt-3 rounded-full" data-testid="panel-become-seller"><Link to="/mercado/anunciar">{seller.status === "pending" ? "Ver status da solicitação" : "Quero vender"}</Link></Button>
        </div>
      )}
    </div>
  );
}

function OrderRow({ o, children }) {
  return (
    <div data-testid={`order-${o.id}`} className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 sm:flex-row sm:items-center">
      <img src={mediaUrl(o.listing_image)} alt="" className="h-16 w-20 rounded-xl object-cover bg-secondary" />
      <div className="flex-1 min-w-0">
        <Link to={`/mercado/anuncio/${o.listing_id}`} className="font-semibold hover:text-primary line-clamp-1">{o.listing_title}</Link>
        <p className="text-xs text-muted-foreground">{o.quantity}x · {brl(o.total)} · {timeAgo(o.created_at)} · {o.fulfillment === "entrega" ? <><Truck className="inline h-3 w-3" /> Entrega</> : <><Store className="inline h-3 w-3" /> Retirada</>}</p>
        <div className="mt-1.5"><Pill map={ORDER_STATUS} value={o.status} testid={`order-status-${o.id}`} /></div>
      </div>
      {children}
    </div>
  );
}

function Orders() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/market/my/orders").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  if (items === null) return <Loader />;
  if (!items.length) return <p className="text-sm text-muted-foreground" data-testid="no-orders">Você ainda não comprou nada. <Link to="/mercado" className="text-primary font-semibold">Explorar o Mercado</Link></p>;
  return <div className="space-y-3">{items.map((o) => (
    <OrderRow key={o.id} o={o}>
      <div className="flex flex-col items-start gap-2 sm:items-end">
        <span className="text-xs text-muted-foreground">Vendedor: {o.seller?.store_name}</span>
        {o.reviewed ? <span className="text-xs font-semibold text-emerald-600">Avaliado</span> : <ReviewDialog order={o} onDone={load} />}
      </div>
    </OrderRow>
  ))}</div>;
}

function ReviewDialog({ order, onDone }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const send = async () => {
    try { await api.post(`/market/orders/${order.id}/review`, { rating, comment }); toast.success("Obrigado pela avaliação!"); setOpen(false); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full" data-testid={`review-btn-${order.id}`}>Avaliar vendedor</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Avaliar {order.seller?.store_name}</DialogTitle></DialogHeader>
        <Stars value={rating} onChange={setRating} size="h-8 w-8" testid="review-stars" />
        <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Como foi sua experiência?" className="rounded-xl" />
        <Button onClick={send} className="rounded-full" data-testid="review-submit">Enviar avaliação</Button>
      </DialogContent>
    </Dialog>
  );
}

const NEXT = { pago: [["enviado", "Marcar como enviado"], ["pronto_retirada", "Pronto para retirada"]], enviado: [["entregue", "Marcar como entregue"]], pronto_retirada: [["entregue", "Marcar como entregue"]] };

function Sales() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/market/my/sales").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const update = async (o, status) => {
    try { await api.post(`/market/orders/${o.id}/status`, { status }); toast.success("Pedido atualizado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (items === null) return <Loader />;
  if (!items.length) return <p className="text-sm text-muted-foreground" data-testid="no-sales">Nenhuma venda ainda.</p>;
  return <div className="space-y-3">{items.map((o) => (
    <OrderRow key={o.id} o={o}>
      <div className="flex flex-col gap-2 sm:items-end">
        <span className="text-xs text-muted-foreground">Comprador: {o.buyer?.name} · {o.buyer?.email}</span>
        {o.address && <span className="max-w-xs text-xs text-muted-foreground sm:text-right">{o.address}</span>}
        <div className="flex flex-wrap gap-2">
          {(NEXT[o.status] || []).filter(([s]) => o.fulfillment === "entrega" ? s !== "pronto_retirada" : s !== "enviado").map(([s, l]) => (
            <Button key={s} size="sm" className="rounded-full" onClick={() => update(o, s)} data-testid={`sale-${s}-${o.id}`}>{l}</Button>
          ))}
        </div>
      </div>
    </OrderRow>
  ))}</div>;
}

function MyListings() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/market/my/listings").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const del = async (l) => { try { await api.delete(`/market/listings/${l.id}`); toast.success("Anúncio removido"); load(); } catch { toast.error("Erro"); } };
  if (items === null) return <Loader />;
  return (
    <div className="space-y-3">
      <Button asChild className="rounded-full gap-1.5" data-testid="new-listing-btn"><Link to="/mercado/anunciar"><Plus className="h-4 w-4" /> Novo anúncio</Link></Button>
      {items.filter((l) => l.status !== "removed").map((l) => (
        <div key={l.id} data-testid={`my-listing-${l.id}`} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3">
          <img src={mediaUrl(l.images?.[0])} alt="" className="h-14 w-16 rounded-xl object-cover" />
          <div className="flex-1 min-w-0">
            <Link to={`/mercado/anuncio/${l.id}`} className="font-semibold text-sm hover:text-primary line-clamp-1">{l.title}</Link>
            <p className="text-xs text-muted-foreground">{brl(l.price)} · {l.stock} em estoque · {l.sold_count || 0} vendido(s)</p>
            {l.status === "rejected" && l.reject_reason && <p className="text-xs text-destructive">Motivo: {l.reject_reason}</p>}
          </div>
          <Pill map={LISTING_STATUS} value={l.status} testid={`my-listing-status-${l.id}`} />
          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => del(l)} data-testid={`delete-listing-${l.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
    </div>
  );
}
